/**
 * Smart Swirl Insights Page
 * Phase 8.2: Dedicated page showing evolution and insights for a Smart Swirl
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { FLAGS } from '../lib/flags';
import Sidebar from '../components/layout/Sidebar';
import SwirlHeader from '../components/swirlspace/SwirlHeader';
import SwirlEvolutionChart from '../components/swirlspace/SwirlEvolutionChart';
import SwirlHighlights from '../components/swirlspace/SwirlHighlights';
import SwirlSummary from '../components/swirlspace/SwirlSummary';
import smartSwirlsService from '../services/smartSwirlsService';
import { Loader } from 'lucide-react';

export default function SmartSwirlInsights() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [swirl, setSwirl] = useState(null);
  const [evolutionData, setEvolutionData] = useState([]);
  const [highlights, setHighlights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isExpanded, setIsExpanded] = useState(true);

  useEffect(() => {
    if (!FLAGS.SMART_SWIRLS_ENABLED) {
      navigate('/swirlspace');
      return;
    }

    if (id && user?.id) {
      loadSwirlData();
    }
  }, [id, user?.id, navigate]);

  const loadSwirlData = async () => {
    setLoading(true);
    try {
      // Load main Swirl data
      const { data: swirlData, error: swirlError } = await smartSwirlsService.getSmartSwirlById(id);
      
      if (swirlError || !swirlData) {
        console.error('Error loading Swirl:', swirlError);
        navigate('/swirlspace');
        return;
      }

      // Verify user owns this Swirl
      if (swirlData.user_id !== user.id) {
        navigate('/swirlspace');
        return;
      }

      setSwirl(swirlData);

      // Load evolution data
      const { data: evolution, error: evolutionError } = await smartSwirlsService.getSwirlEvolution(id);
      if (!evolutionError && evolution) {
        setEvolutionData(evolution);
      }

      // Load highlights
      const { data: highlightsData, error: highlightsError } = await smartSwirlsService.getSwirlEvolutionHighlights(id);
      if (!highlightsError && highlightsData) {
        setHighlights(highlightsData);
      }
    } catch (error) {
      console.error('Error loading Swirl insights:', error);
      navigate('/swirlspace');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-cyan-50">
        <Sidebar
          isExpanded={isExpanded}
          onToggle={() => setIsExpanded(!isExpanded)}
        />
        <main className={`flex-1 overflow-y-auto transition-all duration-500 ease-out ${isExpanded ? 'ml-72' : 'ml-16'}`}>
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <Loader className="w-12 h-12 animate-spin text-purple-600 mx-auto mb-4" />
              <p className="text-neutral-600" style={{ fontFamily: 'Inter, sans-serif' }}>
                Loading insights...
              </p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (!swirl) {
    return (
      <div className="flex h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-cyan-50">
        <Sidebar
          isExpanded={isExpanded}
          onToggle={() => setIsExpanded(!isExpanded)}
        />
        <main className={`flex-1 overflow-y-auto transition-all duration-500 ease-out ${isExpanded ? 'ml-72' : 'ml-16'}`}>
          <div className="p-8 text-center">
            <p className="text-neutral-600 mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>
              Swirl not found
            </p>
            <button
              onClick={() => navigate('/swirlspace')}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              Back to Swirlspace
            </button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-cyan-50">
      <Sidebar
        isExpanded={isExpanded}
        onToggle={() => setIsExpanded(!isExpanded)}
      />
      
      <main className={`flex-1 overflow-y-auto transition-all duration-500 ease-out ${isExpanded ? 'ml-72' : 'ml-16'}`}>
        <div className="swirlspace-root relative min-h-full">
          <div className="relative z-10 p-8 max-w-7xl mx-auto">
            {/* Header */}
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <p className="text-sm text-neutral-500 mb-2" style={{ fontFamily: 'Inter, sans-serif', fontSize: '15px', opacity: 0.7 }}>
                Your curiosity in this space has evolved beautifully.
              </p>
              <SwirlHeader swirl={swirl} />
            </motion.div>

            {/* Evolution Chart */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1, duration: 0.3 }}
              className="mb-6"
            >
              <SwirlEvolutionChart evolutionData={evolutionData} />
            </motion.div>

            {/* Highlights Carousel */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.3 }}
              className="mb-6"
            >
              <SwirlHighlights highlights={highlights} swirlTitle={swirl.title} />
            </motion.div>

            {/* AI Summary */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.3 }}
            >
              <SwirlSummary swirlId={id} evolutionData={evolutionData} />
            </motion.div>
          </div>
        </div>
      </main>
    </div>
  );
}

